import React, { ComponentProps, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { FlatList, Keyboard, Platform, StyleSheet } from 'react-native';

import { AutocompletePrediction } from '@fhs/expo-maps';
import { Box, FormControl, Icon, IconButton, Input } from '@fhs-legacy/universal-components';
import { expandUSStateAbbreviationsInAddress } from 'utils/restaurant';
import { styleToNumber } from 'utils/style/style-to-number';
import { STORE_LOCATOR_SUGGESTION_AT } from 'utils/test-ids';

import {
  Container,
  MainPredictionText,
  OptionsListWrapper,
  StyledAddressInput,
  StyledInputIcon,
  StyledItemText,
  StyledOptionsListItem,
} from './autocomplete-options-dropdown.styles';
import { AutocompleteDropdownProps } from './types';

type ITextInputProps = ComponentProps<typeof Input> & { label: string };

const AutocompleteOptionsDropdown = React.forwardRef<
  HTMLInputElement,
  ITextInputProps & AutocompleteDropdownProps & { initialFocus?: boolean }
>(
  (
    {
      testID,
      clearTextIconTitle,
      errorMessage,
      label,
      accessibilityLabel: fieldAccessibilityLabel,
      accessibilityLabelWhenLabelledByNative,
      fieldLabelledBy,
      fieldDescribedBy,
      accessibilityHint,
      noPredictionsFoundText,
      predictions,
      onInputChange,
      onWebInputTab,
      onSelection,
      value,
      onBlur,
      initialFocus = false,
    },
    ref
  ) => {
    const [isItemSelected, setIsItemSelected] = useState(false);
    const [isInputFocus, setIsInputFocus] = useState(initialFocus);
    const innerWrapperRef = useRef<HTMLElement | null>(null);
    const { formatMessage } = useIntl();

    const setWrapperRef = useCallback(
      (node: HTMLElement | null) => {
        innerWrapperRef.current = node;
        if (ref == null) {
          return;
        }
        if (typeof ref === 'function') {
          (ref as React.RefCallback<HTMLInputElement | null>)(node as HTMLInputElement | null);
        } else {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = node as HTMLInputElement;
        }
      },
      [ref]
    );

    const handleOnSelectItem = (item: AutocompletePrediction) => {
      onSelection({
        address: item.description || '',
        placeId: item.placeId || '',
      });
      setIsItemSelected(true);
      Keyboard.dismiss();
    };

    const handleClearPress = useCallback(() => {
      onInputChange('');
      // Match prior behavior: consumers (e.g. store locator) clear derived state in onAddressSelected / onSelection.
      onSelection({ address: '', placeId: '' });
      setIsItemSelected(false);
      Keyboard.dismiss();
    }, [onInputChange, onSelection]);

    const renderListItem = ({ item, index }: { item: AutocompletePrediction; index: number }) => {
      const suggestionLabel =
        item.description || [item.mainText, item.secondaryText].filter(Boolean).join(', ');
      const suggestionAccessibilityLabel = expandUSStateAbbreviationsInAddress(suggestionLabel);
      return (
        <StyledOptionsListItem
          accessibilityRole="button"
          accessibilityLabel={suggestionAccessibilityLabel}
          _web={{ 'aria-label': suggestionAccessibilityLabel }}
          onPress={() => {
            handleOnSelectItem(item);
            setIsInputFocus(false);
          }}
        >
          <Box
            accessible={false}
            accessibilityElementsHidden
            display="flex"
            flexDirection="row"
            importantForAccessibility="no-hide-descendants"
            justifyContent="flex-start"
            testID={STORE_LOCATOR_SUGGESTION_AT(index)}
            _web={{
              'aria-hidden': true,
              flexDirection: 'column',
            }}
          >
            <MainPredictionText fontSize="xs">{item.mainText}</MainPredictionText>
            <StyledItemText fontSize="xs">{item.secondaryText}</StyledItemText>
          </Box>
        </StyledOptionsListItem>
      );
    };

    const inputRightIcon = (() => {
      const searchAccessibilityLabel = formatMessage({ id: 'searchLocation' });
      if (!value) {
        return <StyledInputIcon variant="search" accessibilityLabel={searchAccessibilityLabel} />;
      }
      const clearLabel = value
        ? `${clearTextIconTitle}. ${value}`
        : clearTextIconTitle;
      return (
        <IconButton
          variant="input"
          iconName="cancel"
          onPress={handleClearPress}
          accessibilityLabel={clearLabel}
          _web={{ cursor: 'pointer' }}
        />
      );
    })();

    const handleInputChange = (inputValue: string) => {
      onInputChange(inputValue);
      setIsItemSelected(false);
    };

    const handleInputFocus = () => {
      setIsInputFocus(true);
    };

    const resolvedAccessibilityLabel =
      fieldLabelledBy && Platform.OS === 'web'
        ? undefined
        : fieldLabelledBy
          ? accessibilityLabelWhenLabelledByNative ?? fieldAccessibilityLabel ?? label
          : fieldAccessibilityLabel ?? label;

    const linkedHeadingA11yProps =
      fieldLabelledBy && Platform.OS !== 'web'
        ? { accessibilityLabelledBy: fieldLabelledBy }
        : {};

    const describedByA11yProps =
      fieldDescribedBy && Platform.OS !== 'web'
        ? { accessibilityDescribedBy: fieldDescribedBy }
        : {};

    /**
     * With `InputRightElement`, Native Base puts `wrapperRef` on a flex `Box` and the real
     * `<input>` is inside it. `_web` aria props on `Input` therefore land on the wrapper, so
     * SR never associates described-by / labelled-by with the focusable control. Sync onto the
     * actual input on web only.
     */
    useLayoutEffect(() => {
      if (Platform.OS !== 'web' || (!fieldLabelledBy && !fieldDescribedBy && !onWebInputTab)) {
        return;
      }

      let inputElForCleanup: HTMLInputElement | null = null;
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Tab' && !event.shiftKey) {
          onWebInputTab?.(event);
        }
      };

      const getInnerInput = () => {
        const root = innerWrapperRef.current;
        if (!root) {
          return null;
        }
        return root.tagName === 'INPUT'
          ? (root as HTMLInputElement)
          : root.querySelector?.('input') ?? null;
      };

      const applyToInnerInput = () => {
        const inputEl = getInnerInput();
        if (!inputEl) {
          return;
        }
        if (fieldLabelledBy) {
          inputEl.setAttribute('aria-labelledby', fieldLabelledBy);
          inputEl.removeAttribute('aria-label');
        } else {
          inputEl.removeAttribute('aria-labelledby');
          inputEl.removeAttribute('aria-label');
        }
        if (fieldDescribedBy) {
          inputEl.setAttribute('aria-describedby', fieldDescribedBy);
        } else {
          inputEl.removeAttribute('aria-describedby');
        }

        if (inputElForCleanup !== inputEl) {
          inputElForCleanup?.removeEventListener('keydown', handleKeyDown);
          inputEl.addEventListener('keydown', handleKeyDown);
          inputElForCleanup = inputEl;
        }
      };

      applyToInnerInput();
      const raf = requestAnimationFrame(applyToInnerInput);

      return () => {
        cancelAnimationFrame(raf);
        const inputEl = getInnerInput() ?? inputElForCleanup;
        inputElForCleanup?.removeEventListener('keydown', handleKeyDown);
        inputEl?.removeAttribute('aria-labelledby');
        inputEl?.removeAttribute('aria-label');
        inputEl?.removeAttribute('aria-describedby');
      };
    }, [fieldDescribedBy, fieldLabelledBy, isInputFocus, onWebInputTab]);

    return (
      <Container>
        <FormControl isInvalid={!!errorMessage} margin={0}>
          <StyledAddressInput
            testID={testID}
            placeholder={label}
            accessibilityLabel={resolvedAccessibilityLabel}
            accessibilityHint={accessibilityHint}
            {...linkedHeadingA11yProps}
            {...describedByA11yProps}
            value={value}
            InputRightElement={inputRightIcon}
            onChangeText={handleInputChange}
            wrapperRef={setWrapperRef}
            onFocus={handleInputFocus}
            onBlur={onBlur}
          />
          <FormControl.ErrorMessage leftIcon={<Icon variant="error" size="xs" />}>
            {errorMessage}
          </FormControl.ErrorMessage>
        </FormControl>
        {isInputFocus && (
          <OptionsListWrapper>
            {value &&
              value.length > 2 &&
              !isItemSelected &&
              (!predictions?.length ? (
                <StyledOptionsListItem disabled>
                  <StyledItemText fontSize="2xs">{noPredictionsFoundText}</StyledItemText>
                </StyledOptionsListItem>
              ) : (
                <FlatList
                  style={styles.flatList}
                  data={predictions}
                  renderItem={renderListItem}
                  keyExtractor={(item: AutocompletePrediction) => item.placeId}
                  scrollEnabled={false}
                  keyboardShouldPersistTaps="always"
                  accessible={Platform.OS !== 'web'}
                  accessibilityRole={Platform.OS === 'web' ? 'list' : undefined}
                />
              ))}
          </OptionsListWrapper>
        )}
      </Container>
    );
  }
);

const styles = StyleSheet.create({
  flatList: {
    borderBottomLeftRadius: styleToNumber(Styles.borderRadius),
    borderBottomRightRadius: styleToNumber(Styles.borderRadius),
    overflow: 'hidden',
  },
});

export default AutocompleteOptionsDropdown;
